import type { PostHogConfig } from 'posthog-js';
import type { Edition, SessionUser } from '../auth/passwordSession';
import { getConfig } from '../config/runtimeConfig';

/**
 * Product analytics (PostHog), community edition only — the whole of it.
 *
 * AUTOMATIC BY DESIGN (decided 2026-09-28): page views, time on page, clicks,
 * form submits, rage clicks and uncaught errors are captured by the library,
 * and no page carries tracking code. The server sends the few events only it
 * can see. Everything the front end needs to decide lives in this file.
 *
 * What is deliberately left OFF, because each would cost the viewer:
 *   - heatmaps: a document-wide mousemove listener, running through every pan
 *   - network and performance capture: would see every tile request
 *   - session recording: needs consent in the signup terms first, and would
 *     have to pause on the viewer
 * The map carries `ph-no-capture` (viewer/Viewport.tsx), and so does the
 * annotation overlay drawn over it (features/study/AnnotationOverlay.tsx),
 * where every drawing and counting click lands and whose labels and handles
 * take a pointer cursor, which autocapture otherwise records. So a pan, a
 * polygon's clicks or a count are neither clicks nor rage clicks.
 *
 * Every session change is a full page load through main.tsx's boot (sign-in
 * lands with `window.location.assign`, sign-out and account deletion reload),
 * so identifying and forgetting the person happens here, once per load.
 */
export function startAnalytics(edition: Edition, me: SessionUser | null | undefined): void {
  const { posthogKey, posthogHost } = getConfig();
  if (edition !== 'community' || !posthogKey) return;
  whenIdle(() => {
    // Its own chunk, fetched once the page has settled: a link straight into a
    // slide spends its first seconds on tiles, not on this. `no-external` is the
    // build that never downloads code from PostHog — the app shares its origin
    // with the session cookie, so nothing we did not ship runs on it.
    import('posthog-js/no-external')
      .then(({ default: posthog }) => {
        posthog.init(posthogKey, { ...OPTIONS, api_host: posthogHost });
        if (me?.userId) {
          const person = personOf(me);
          if (posthog.get_distinct_id() !== me.userId) posthog.identify(me.userId, person);
          // Only when something changed (new initials, or a browser identified
          // before they were sent): setting them on every load is a billed
          // `$set` each time. PostHog keeps what it last set, until a reset.
          else if (changed(posthog.get_property('$stored_person_properties'), person)) posthog.setPersonProperties(person);
        }
        // Signed out since the last load: the next person on this browser must
        // not inherit the last one's identity.
        else if (posthog.get_property('$user_state') === 'identified') posthog.reset();
      })
      // The chunk can be gone (an old tab after a deploy) or unreachable.
      // Analytics is never worth an error.
      .catch(() => {});
  });
}

/**
 * What PostHog knows about the person besides their user id: their initials
 * as `name`, so its people list reads "A.L." instead of an id — never the
 * email or the full name (decided 2026-09-28). Their user id finds them here.
 *
 * TODO (administration): when the dashboard gets a page per user, add its
 * URL as a person property (e.g. `admin_url`), so a PostHog person is one
 * click from who they are without anything more leaving for PostHog.
 */
function personOf(me: SessionUser): Record<string, string | boolean> {
  const person: Record<string, string | boolean> = { superadmin: me.superadmin };
  const initials = initialsOf(me);
  if (initials) person.name = initials;
  return person;
}

/** "Ana María López" → "A.L.": first and last name, one letter each. */
export function initialsOf(me: Pick<SessionUser, 'displayName' | 'givenName' | 'familyName'>): string {
  const whole = me.displayName?.includes('@') ? '' : (me.displayName ?? '');
  const words = me.givenName || me.familyName ? [me.givenName, me.familyName] : whole.trim().split(/\s+/);
  const names = words.map((w) => w?.trim()).filter((w): w is string => !!w);
  const picked = names.length > 2 ? [names[0], names[names.length - 1]] : names;
  return picked.map((w) => `${Array.from(w)[0].toUpperCase()}.`).join('');
}

function changed(stored: Record<string, unknown> | undefined, person: Record<string, unknown>): boolean {
  return Object.entries(person).some(([k, v]) => stored?.[k] !== v);
}

const OPTIONS: Partial<PostHogConfig> = {
  ui_host: 'https://us.posthog.com',
  // Pinned: behaviours PostHog puts behind a later date arrive only when this
  // moves. It does not freeze everything — an upgrade still needs its changelog.
  defaults: '2026-08-30',
  // Guests and share-link visitors stay anonymous (and cheaper) until they sign in.
  person_profiles: 'identified_only',
  capture_heatmaps: false,
  capture_performance: false,
  disable_session_recording: true,
  disable_surveys: true,
  capture_exceptions: true,
  // No feature flags: saves a request on every load.
  advanced_disable_flags: true,
  disable_external_dependency_loading: true,
  before_send: (event) =>
    event && { ...event, properties: scrub(event.properties), $set: scrub(event.$set), $set_once: scrub(event.$set_once) },
};

/**
 * Takes the secrets out of an event before it leaves the browser.
 *
 * Share codes (`/s/stu/<code>`) and invite tokens (`?token=`) are bearer
 * credentials — the server keeps share codes only hashed — and they sit in the
 * very URLs an automatic page view reports, in link addresses a click records,
 * and inside `?return=` of a sign-in bounce, percent-encoded. Emails show up
 * as the text of a clicked member row. All of it is replaced wherever it
 * appears, in any property, rather than in a list of known fields that the
 * library can add to. Events are JSON trees, so the walk needs no depth limit.
 */
export function scrub<T>(value: T): T {
  if (typeof value === 'string') {
    return value.replace(SHARE_CODE, '$1:code').replace(INVITE_TOKEN, '$1:token').replace(EMAIL, ':email') as T;
  }
  if (!value || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(scrub) as T;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) out[k] = scrub(v);
  return out as T;
}

const SLASH = '(?:/|%2F)';
// Any kind and any length on purpose: the segment after /s/<kind>/ is the code
// (community/guestPaths.ts has today's exact shape), and a new kind or a longer
// code must not quietly start leaking. Over-matching costs nothing.
const SHARE_CODE = new RegExp(`(${SLASH}s${SLASH}[a-z]+${SLASH})[0-9A-Za-z]+`, 'gi');
const INVITE_TOKEN = /((?:[?&]|%3F|%26)token(?:=|%3D))[A-Za-z0-9_-]+/gi;
// Bounded (RFC 5321 lengths), so a long run with no '@' costs linear time on
// the main thread rather than rescanning from every position.
const EMAIL = /[A-Z0-9._%+-]{1,64}@[A-Z0-9.-]{1,253}\.[A-Z]{2,24}/gi;

function whenIdle(run: () => void): void {
  if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}
